import { forwardRef } from "react";

import { useHistory } from "react-router-dom";

import Box from "@/components/Box";
import Circle from "@/components/Circle";
import { CMP_LEARNING_HUB_EVENT_URL } from "@/core/urls";
import useCmpRelatedUrl from "@/hooks/organisations/useCmpRelatedUrl";
import useWindowSize from "@/hooks/use-window-size";
import Badge from "@/tpo/Badge";
import Group from "@/tpo/Group";
import Stack from "@/tpo/Stack";
import ButtonV2 from "@/v2/Buttons";

import classes from "./EventCard.module.css";

const EventCard = forwardRef(
  (
    { shortDescription, title, startTime, location, type, slug, eventBadges, cardLayout = false },
    ref
  ) => {
    const history = useHistory();

    const { width } = useWindowSize();
    const isMobile = width < 768;

    const { resolvedUrls } = useCmpRelatedUrl();

    if (cardLayout || isMobile) {
      return (
        <Stack
          borderRadius={20}
          bg="white"
          p={40}
          gap={20}
          height="100%"
          onClick={() => {
            history.push(resolvedUrls[CMP_LEARNING_HUB_EVENT_URL].replace(":slug", slug));
          }}
        >
          <Stack gap={8}>
            <Box
              fontFamily="gilroyBold"
              fontSize={[24]}
              sx={{
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
                overflow: "hidden"
              }}
            >
              {title}
            </Box>
            <Badge bg="purple" color="white" mr="auto">
              {type.replace("_", " ")}
            </Badge>
          </Stack>
          <Stack>
            <Box fontFamily="gilroyMedium" color="purple">
              {location}
            </Box>
            <Box fontFamily="gilroyBold" color="purple" whiteSpace="nowrap">
              {new Date(startTime).toLocaleDateString(undefined)}
            </Box>
            <Box fontFamily="gilroyBold" color="purple" whiteSpace="nowrap">
              {new Date(startTime).toLocaleTimeString(undefined, {
                timeZoneName: "short"
              })}
            </Box>
          </Stack>
          <Box flexGrow={1} minHeight={0} className={classes.truncateText}>
            {shortDescription}
          </Box>
          <Group justifyContent="space-between" mt="auto">
            {!!eventBadges?.length && (
              <Group flexWrap="wrap" gap={6}>
                {eventBadges.map(eb => (
                  <Circle
                    size={52}
                    fontFamily="gilroyBold"
                    fontSize={[14, 14, 16]}
                    key={eb.id}
                    borderWidth={2}
                    backgroundColor="transparent"
                    withBorder
                    flexDirection="column"
                    gap={0}
                  >
                    <Box fontSize={12} lineHeight="100%" textTransform="uppercase">
                      {eb.badge.name}
                    </Box>
                    <Box fontSize={24} lineHeight="100%">
                      {eb.points}
                    </Box>
                  </Circle>
                ))}
              </Group>
            )}
            <ButtonV2
              color="dark"
              ml="auto"
              mt="auto"
              size="xs"
              sx={{
                px: 20,
                fontSize: 12
              }}
            >
              register
            </ButtonV2>
          </Group>
        </Stack>
      );
    }

    return (
      <tr
        onClick={() => {
          history.push(resolvedUrls[CMP_LEARNING_HUB_EVENT_URL].replace(":slug", slug));
        }}
        className={classes.tr}
        ref={ref}
        style={{
          height: "100%"
        }}
      >
        <Box
          bg="white"
          as="td"
          pl={40}
          borderTopLeftRadius={20}
          borderBottomLeftRadius={20}
          py={40}
          height="100%"
        >
          <Stack height="100%">
            <Stack gap={8} mb={40}>
              <Box fontFamily="gilroyBold" fontSize={[24]}>
                {title}
              </Box>
              <Badge bg="purple" color="white" mr="auto">
                {type.replace("_", " ")}
              </Badge>
            </Stack>
            {!!eventBadges?.length && (
              <Group flexWrap="wrap" gap={6}>
                {eventBadges.map(eb => (
                  <Circle
                    size={52}
                    fontFamily="gilroyBold"
                    fontSize={[14, 14, 16]}
                    key={eb.id}
                    borderWidth={2}
                    backgroundColor="transparent"
                    withBorder
                    flexDirection="column"
                    gap={0}
                  >
                    <Box fontSize={12} lineHeight="100%" textTransform="uppercase">
                      {eb.badge.name}
                    </Box>
                    <Box fontSize={24} lineHeight="100%">
                      {eb.points}
                    </Box>
                  </Circle>
                ))}
              </Group>
            )}
          </Stack>
        </Box>
        <Box bg="white" as="td" py={40} height="100%" px={20}>
          <Stack height="100%">
            <Box fontFamily="gilroyMedium" color="purple">
              {location}
            </Box>
            <Box fontFamily="gilroyBold" color="purple" whiteSpace="nowrap">
              {new Date(startTime).toLocaleDateString(undefined)}
            </Box>
            <Box fontFamily="gilroyBold" color="purple" whiteSpace="nowrap">
              {new Date(startTime).toLocaleTimeString(undefined, {
                timeZoneName: "short"
              })}
            </Box>
          </Stack>
        </Box>
        <Box
          py={40}
          bg="white"
          as="td"
          pr={40}
          borderTopRightRadius={20}
          borderBottomRightRadius={20}
          height="100%"
        >
          <Stack height="100%">
            <Box className={classes.truncateText}>{shortDescription}</Box>
            <ButtonV2
              color="dark"
              ml="auto"
              mt="auto"
              size="xs"
              sx={{
                px: 20,
                fontSize: 12
              }}
            >
              register
            </ButtonV2>
          </Stack>
        </Box>
      </tr>
    );
  }
);

export default EventCard;
